import { t } from "@travily/i18n";
import { accountSettingsProfilePath } from "@travily/routes";
import type Image from "@travily/types/shared/Image";
import { ExpandableText } from "@ui/components/ExpandableText";
import { InfoPill } from "@ui/components/InfoPill";
import { OutlineButton } from "@ui/components/OutlineButton";
import { tv } from "@ui/lib/tv";
import { AccountAvatar } from "@/components/AccountAvatar/AccountAvatar";
import { ActionBar } from "@/components/ActionBar/ActionBar";
import { BACKEND_STATUS_MAP, FriendshipButton } from "@/components/FriendshipButton/FriendshipButton";
import { ProfileActionMenu } from "@/components/ProfileActionMenu/ProfileActionMenu";
import { messageThreadHref } from "@/lib/routes";

const avatarWrapper = tv({
  base: "flex -rotate-6 shrink-0 items-center justify-center bg-white",
  variants: {
    size: {
      "88": "size-[88px] rounded-2xl p-[7px]",
      "120": "size-[120px] rounded-[25px] p-2.5",
    },
  },
});

export interface IdentityTrips {
  current_location: { name: string } | null;
  next_location: { name: string } | null;
}

/** The slice of a profile account the identity header renders. */
export interface IdentityAccount {
  profile_type: string;
  handle: string;
  display_name: string;
  bio: string | null;
  avatar: Image | null;
  initial: string;
  cover_image: Image | null;
  friendship_status: "none" | "pending_sent" | "pending_received" | "accepted";
  can: { edit: boolean };
  /**
   * Trip pill data; pass with null locations when the account simply has no
   * trips. Omit entirely for account types without trips (community) — the
   * pill/action row is then hidden on mobile instead of reserving space.
   */
  trips?: IdentityTrips;
}

export interface IdentityProps {
  account: IdentityAccount;
  canDock?: boolean;
  onAboutAccount?: () => void;
  onAcceptFriendship?: () => void;
  onDeclineFriendship?: () => void;
  onFriendshipAction?: () => void;
  /** Called when the user clicks "Share this profile". */
  onShareProfile?: () => void;
  onUnfriend?: () => void;
  /** Opens the account's friends list modal. */
  onViewFriends?: () => void;
}

/**
 * Profile identity header shared by the individual, family and community
 * profile pages: avatar, name, bio, trip pills (when the account type has
 * trips) and the friendship/message/edit action row.
 */
export function Identity({
  account,
  canDock,
  onAboutAccount,
  onAcceptFriendship,
  onDeclineFriendship,
  onFriendshipAction,
  onShareProfile,
  onUnfriend,
  onViewFriends,
}: IdentityProps) {
  const friendshipStatus = BACKEND_STATUS_MAP[account.friendship_status ?? "none"] ?? "default";
  const isDisabled = friendshipStatus === "invited";
  const hasCoverImage = !!account.cover_image;

  const infoPillItems: { label: string; value: string }[] = [];
  if (account.trips?.current_location)
    infoPillItems.push({
      label: t("common.identity.trip_in"),
      value: account.trips.current_location.name,
    });
  if (account.trips?.next_location)
    infoPillItems.push({
      label: t("common.identity.trip_next"),
      value: account.trips.next_location.name,
    });

  const actionBarProps = {
    canDock,
    canEdit: account.can.edit,
    friendshipStatus,
    handle: account.handle,
    isDisabled,
    onAboutAccount,
    onAcceptFriendship,
    onDeclineFriendship,
    onFriendshipAction,
    onShareProfile,
    onUnfriend,
    onViewFriends,
  } as const;

  return (
    <>
      <div className="flex w-full items-start md:contents">
        <div
          className={avatarWrapper({
            className: `md:hidden ${hasCoverImage ? "-mt-[41px]" : "-mt-[88px]"}`,
            size: "88",
          })}
        >
          <AccountAvatar
            account={{ profile_type: account.profile_type, avatar: account.avatar, initial: account.initial }}
            className="size-full rounded-xl"
            size="74"
          />
        </div>
        <div
          className={avatarWrapper({
            className: `hidden md:flex ${hasCoverImage ? "-mt-[60px]" : "mt-8"}`,
            size: "120",
          })}
        >
          <AccountAvatar
            account={{ profile_type: account.profile_type, avatar: account.avatar, initial: account.initial }}
            className="size-full rounded-3xl"
            size="100"
          />
        </div>
        <div className={`ml-auto flex items-center gap-2 md:hidden ${hasCoverImage ? "mt-4" : "-mt-4"}`}>
          {account.can.edit ? (
            <OutlineButton href={accountSettingsProfilePath()} size="36">
              {t("common.profile.edit_profile")}
            </OutlineButton>
          ) : (
            <FriendshipButton
              isDisabled={isDisabled}
              menuPlacement="bottom end"
              onAcceptFriendship={onAcceptFriendship}
              onDeclineFriendship={onDeclineFriendship}
              onPress={onFriendshipAction}
              onUnfriend={onUnfriend}
              size="36"
              status={friendshipStatus}
            />
          )}
          <ProfileActionMenu
            canEdit={account.can.edit}
            friendshipStatus={friendshipStatus}
            messageHref={messageThreadHref(account.handle)}
            onAboutAccount={onAboutAccount}
            onShareProfile={onShareProfile}
            onUnfriend={onUnfriend}
            onViewFriends={onViewFriends}
          />
        </div>
      </div>
      <div className="mt-3 flex w-full flex-col gap-1 md:gap-0">
        <h1 className="text-display-xs lg:text-display-md text-primary">{account.display_name}</h1>
        <div
          className={
            account.trips
              ? "order-3 mt-3 flex flex-col items-start gap-2 md:order-none md:mt-1.5 md:flex-row md:items-center md:justify-center lg:mt-2 lg:gap-3"
              : "hidden md:mt-1.5 md:flex md:items-center md:justify-center lg:mt-2 lg:gap-3"
          }
        >
          {infoPillItems.length > 0 && <InfoPill items={infoPillItems} width="responsive" />}
          <div className="hidden items-center gap-3 md:flex lg:hidden">
            <ActionBar {...actionBarProps} size="36" />
          </div>
          <div className="hidden items-center gap-3 lg:flex">
            <ActionBar {...actionBarProps} size="40" />
          </div>
        </div>
        {account.bio && (
          <ExpandableText
            className={
              account.trips
                ? "order-2 text-base md:order-none md:mt-3 lg:mt-4 lg:text-xl text-secondary"
                : "text-base md:mt-3 lg:mt-4 lg:text-xl text-secondary"
            }
            lines={3}
          >
            {account.bio}
          </ExpandableText>
        )}
      </div>
    </>
  );
}
